import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, Input, ViewChild } from '@angular/core';
import { WidgetService } from 'src/core/services/widget.service';
import { DeviceService } from 'src/core/services/device.service';
import { LoadingService } from 'src/core/services/loading.service';
import { SelectedsystemService } from 'src/core/services/selectedsystem.service';
import { ChatService } from 'src/core/services/chat.service';
import { Table, TableModule } from 'primeng/table';
import { DaterangeService } from 'src/core/services/daterange.service';
import { defer, iif, map, merge, Observable, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { DataService } from 'src/core/services/data.service';
import { NgShuiModule } from '@shui/angular';
import { TranslocoModule } from '@jsverse/transloco';
import { selectedWidget, workareaSize } from 'src/core/types/workarea.types';
import { FormsModule } from '@angular/forms';
import { SiteVisitsResponse, VisitsData } from 'src/core/types/servicehistory.types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiResponse } from 'src/core/types/general.types';
import { SortEvent } from 'primeng/api';
import { SelectedContentService } from 'src/core/services/selectedcontent.service';
import { environment } from 'src/environments/environment';
import { shouldMarkPromptAsOptionAnswer } from 'src/core/utils/chat-option-answer.util';

@Component({
  selector: 'app-activities',
  imports: [CommonModule, TableModule, NgShuiModule, FormsModule, TranslocoModule],
  templateUrl: './activities.component.html',
  styleUrl: './activities.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ActivitiesComponent {

  //Table Variables
  @ViewChild('table') table: Table;
  readonly businessPartnerMode = environment.businessPartnerMode;

  @Input() tabs: any[] = [];

  constructor(
    private widgetService: WidgetService,
    private deviceService: DeviceService,
    private loadingService: LoadingService,
    private selectedSystemService: SelectedsystemService,
    private chatService: ChatService,
    private daterangeService: DaterangeService,
    private dataService: DataService,
    private selectedContentService: SelectedContentService
  ) { }

  onMaximize = this.widgetService.onMaximize.bind(this.widgetService);
  onMinimize = this.widgetService.onMinimize.bind(this.widgetService);
  onExpand = (event: any) => {
    if(this.workareaSize !== "fullscreen") {
      this.widgetService.onExpand();
    } else {
      this.widgetService.$workareaSize.next("large");
    }
  };  
  switchTab = this.widgetService.switchTab.bind(this.widgetService);
  changeRange = (range: number, type: 'systemHealth' | 'serviceHistory') => {
    this.daterangeService.changeRange(range, type);
  };

  screenInfo$ = this.deviceService.screenInfo$;
  isLoading: boolean;
  isError: boolean;
  isDeviceUI: boolean;
  deviceInformation: any;
  searchTerm: string = '';
  visits: any[] = [];
  selectedRange: number;
  selectedWidget: string = "None";
  displayedTabs: any[] = [];
  activeTab: string = "Activities";
  workareaSize: workareaSize = "small";
  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.loadingService.$isVisitsLoading.subscribe((loading) => {
      this.isLoading = loading;
    });
    this.loadingService.$isVisitsError.subscribe((error) => {
      this.isError = error;
    });

    //Check which tabs to display
    this.widgetService.$selectedWidget.subscribe({
      next: (widget: selectedWidget) => {
        this.selectedWidget = widget;
        this.displayedTabs = this.tabs.filter(tab => tab.selectedWidget === widget);

      }
    });

    //Check which tab is currently active
    this.widgetService.$activeTab.subscribe((tab) => {
      if (tab) {
        this.activeTab = tab;
      }
      else {
        this.activeTab = this.displayedTabs[0]?.name;
      }
    });

    //Need to know the current workarea size as well to display correct icons
    this.widgetService.$workareaSize.subscribe({
      next: (size: workareaSize) => {
        this.workareaSize = size;
      }
    });

    //Listening to changed Daterange
    const range$ = this.daterangeService.$selectedRangeServiceHistory;

    // Trigger when range changes (normal), or when user clicks refresh (force fetch)
    const triggers$ = merge(
      range$.pipe(map(rangeValue => ({ rangeValue, force: false }))),
      this.selectedSystemService.$refreshVisits.pipe(
        withLatestFrom(range$),
        map(([, rangeValue]) => ({ rangeValue, force: true }))
      )
    );

    triggers$.pipe(
      tap(() => {
        // ✅ Reset before API call
        this.loadingService.$isVisitsLoading.next(true);
        this.loadingService.$isVisitsError.next(false);
        this.visits = null;
      }),
      switchMap(({ rangeValue, force }) => defer(() => {
        const now = new Date();
        const lastPulled = this.selectedSystemService.VisitsPullDate;
        const pulledFor = this.selectedSystemService.visitsPulledFor;

        const isDevice = this.selectedContentService.selection.kind == "device";
        this.isDeviceUI = isDevice;
        const serial = this.selectedContentService.currentSystem?.SerialNumber || '';
        const material = this.selectedContentService.currentSystem?.MaterialNumber || '';
        const funcloc = this.selectedContentService.currentSite?.Id;

        const cacheKey = isDevice ? (serial + material) : funcloc;
        const shouldFetch = force ||
          !lastPulled ||
          (now.getTime() - new Date(lastPulled).getTime()) > 5 * 60 * 1000 ||
          pulledFor !== cacheKey;

        type VisitsResponse = VisitsData | SiteVisitsResponse;

        const apiCall$: Observable<ApiResponse<VisitsResponse>> = isDevice
          ? this.dataService.getVisits(material, serial).pipe(
            map((result): ApiResponse<VisitsResponse> => result)
          )
          : this.dataService.getVisitsSite(funcloc).pipe(
            map((result): ApiResponse<VisitsResponse> => result)
          );


        return iif(
          () => shouldFetch,
          apiCall$.pipe(
            tap(({ response, status }: ApiResponse<VisitsResponse>) => {
              console.log(force ? 'Forced refresh of visits data' : 'Fetching new visits data');
              this.selectedSystemService.VisitsPullDate = now;
              this.selectedSystemService.visitsPulledFor = cacheKey;
              if (status === 200) {
                this.selectedSystemService.$visits.next(response);
              } else if (status === 204) {
                this.selectedSystemService.$visits.next(null);
                this.loadingService.$isVisitsEmpty.next(true);
              }
            })
          ),
          this.selectedSystemService.$visits.pipe(
            take(1),
            tap(() => console.log('Using cached visits data')),
            map((cached): ApiResponse<VisitsResponse> => ({
              response: cached,
              status: 200 // or some fixed value to simulate a successful cached response
            }))
          )
        ).pipe(
          map(({ response, status }: ApiResponse<VisitsResponse>) => ({ response, rangeValue }))
        );
      })),
      // 👇 Auto-unsubscribe when the component is destroyed
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(
      {
        next: ({ response, rangeValue }) => {

          this.selectedRange = rangeValue;

          if (this.selectedContentService.selection.kind == "device") {
            let temp = response as VisitsData;

            let data = temp?.visits;

            const inter = data?.filter(f => f.time_window !== null) || [];
            this.visits = inter.filter(f => f.time_window.some(tw => tw === (rangeValue === 365 ? 120 : rangeValue)));
            this.loadingService.$isVisitsLoading.next(false);

          } else {

            let temp = response as SiteVisitsResponse;
            const mapped = temp.visits;
            

            const inter = mapped?.filter(f => f.time_window !== null) || [];
            this.visits = inter.filter(f => f.time_window.some(tw => tw === (rangeValue === 365 ? 120 : rangeValue)));
            this.loadingService.$isVisitsLoading.next(false);
          }

        },
        error: (error) => {
          console.error('Error fetching spare parts data:', error);
          this.visits = [];
          this.loadingService.$isVisitsError.next(true);
          this.loadingService.$isVisitsLoading.next(false);
        }
      }
    );
  }




  askChat(id: string) {

    let prompt = `What can you tell me about the Service Ticket with ID ${id}?`;

    if (shouldMarkPromptAsOptionAnswer(this.chatService.messages?.value)) {
      this.chatService.addMessage(prompt, true, undefined, true);
      return;
    }

    this.chatService.addMessage(prompt, true);

  }

  onSearchApply(event: any) {
    this.searchTerm = event.value?.toLowerCase() || '';
  }

  highlight(text: string): string {
    if (!this.searchTerm || !text) return text;

    const escaped = this.searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escaped})`, 'gi');
    return text.replace(regex, '<mark class="hl">$1</mark>');
  }

  retrieveTotalTravel() {
    const data = this.table?.filteredValue || this.visits;
    return data?.reduce((total, visit) => {
      return total + (visit.travel_time || 0);
    }, 0).toFixed(2) || 0;
  }

  retrieveTotalLabor() {
    const data = this.table?.filteredValue || this.visits;
    return data?.reduce((total, visit) => {
      return total + (visit.labor_time || 0);
    }, 0).toFixed(2) || 0;
  }

  refresh() {
    this.selectedSystemService.$refreshVisits.next();
  }

  customSort(event: SortEvent) {
    const field = event.field;
    const order = event.order;

    event.data.sort((a, b) => {
      let val1: any;
      let val2: any;

      switch (field) {
        case 'start': // assuming this is a date field
          val1 = new Date(a.start);
          val2 = new Date(b.start);
          break;
        case 'end': // assuming this is a date field
          val1 = new Date(a.end);
          val2 = new Date(b.end);
          break;
        
        case 'serial': // string field
          val1 = a.serial_number?.toLowerCase() ?? '';  
          val2 = b.serial_number?.toLowerCase() ?? '';
          break;

        case 'activity': // string field
          val1 = a.activity?.toLowerCase() ?? '';
          val2 = b.activity?.toLowerCase() ?? '';
          break;
        case 'type': // string field
          val1 = a.type?.toLowerCase() ?? '';
          val2 = b.type?.toLowerCase() ?? '';
          break;
        case 'noti': // string field
          val1 = a.noti_id?.toLowerCase() ?? '';
          val2 = b.noti_id?.toLowerCase() ?? '';
          break;

        case 'travel':
          val1 = a.travel_time || 0;
          val2 = b.travel_time || 0;
          break;
        case 'labor':
          val1 = a.labor_time || 0;
          val2 = b.labor_time || 0;
          break;

        default:
          val1 = a[field];
          val2 = b[field];
          break;
      }

      let result = 0;
      if (val1 < val2) result = -1;
      else if (val1 > val2) result = 1;

      return result * order;
    });
  }

}
